import React, { useState } from 'react';
import { Button, notification, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { Content } from 'antd/lib/layout/layout';

import { useAppDispatch, useAppSelector } from 'store';
import { MainLayout } from 'components/Layout/MainLayout';
import { useQuery } from 'hooks/useQuery';
import { useDeepEffect } from 'utils/useDeepEffect';
import { StickyConfirmModal } from 'components/modals/StickyConfirmModal';
import {
  loadNewsList,
  setPublishNew,
  setUnPublishNew,
  removeNewsItem,
} from 'store/slices/news/actions';
import { PageTitles } from 'constants/pageTitles';
import { INewsItem } from 'store/slices/news/interfaces';
import { canBePublished, canBeUnPublished } from 'utils/rights';
import { StatusesEnum } from 'constants/status';
import { LangEnum } from 'constants/lang';
import { NewsTopicEnum } from 'constants/newsTopic';

import { Filters } from './components/Filters';
import { ListTable } from './components/ListTable';

export const List = () => {
  const [sort, setSort] = React.useState(null);
  const dispatch = useAppDispatch();
  const query = useQuery();

  const { list, total, limit } = useAppSelector(state => state.news);
  const { b2cUrl, b2bUrl } = useAppSelector(state => state.configSlice);
  const loadNews = () => {
    dispatch(
      loadNewsList({
        ...query,
        sort,
      })
    );
  };

  const [modalIsOpen, setModalIsOpen] = useState(null);
  const [currentItem, setCurrentItem] = useState<INewsItem | null>(null);

  useDeepEffect(() => {
    loadNews();
  }, [query, sort]);

  const modalCloseHandler = () => {
    setModalIsOpen(false);
    setCurrentItem(null);
  };

  const handleRemove = (id: number) =>
    dispatch(removeNewsItem({ id }))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно удалена',
        });
        loadNews();
        modalCloseHandler();
      });

  const handlePublish = id =>
    dispatch(setPublishNew(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно опубликована',
        });
        loadNews();
        modalCloseHandler();
      });

  const handleUnPublish = id =>
    dispatch(setUnPublishNew(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно снята с публикации',
        });
        loadNews();
        modalCloseHandler();
      });

  const getActions = item => [
    item.status === StatusesEnum.PUBLISHED && {
      element: (
        <a
          target='_blank'
          rel='noreferrer nofollow'
          href={`${item.topic === NewsTopicEnum.Tourist ? b2cUrl : b2bUrl}/${
            item.lang
          }/news/${item.id}`}
        >
          На сайте
        </a>
      ),
    },
    {
      element: <Link to={`/news/${item.id}/edit`}>Редактировать</Link>,
    },
    item.lang === LangEnum.ru && {
      element: (
        <Link to={`/news/${item.id}/add-translation`}>
          Создать копию для перевода
        </Link>
      ),
    },
    canBePublished(item) && {
      element: <Typography.Text>Опубликовать</Typography.Text>,
      handler: () => handlePublish(item.id),
    },
    canBeUnPublished(item) && {
      element: <Typography.Text>Снять с публикации</Typography.Text>,
      handler: () => handleUnPublish(item.id),
    },
    {
      element: <Typography.Text type='danger'>Удалить</Typography.Text>,
      handler: () => {
        if (item.status === StatusesEnum.PUBLISHED) {
          notification.error({
            message: 'Необходимо снять сущность с публикации',
          });
        } else {
          setModalIsOpen(true);
          setCurrentItem(item);
        }
      },
    },
  ];

  return (
    <MainLayout
      pageTitle={PageTitles['news']}
      extraButtonsList={[
        <Link key={1} to='/news/add'>
          <Button type='primary'>Создать</Button>
        </Link>,
      ]}
      aside={<Filters />}
    >
      <Content>
        <ListTable
          data={list}
          total={total}
          getActions={getActions}
          limit={limit}
          setSort={setSort}
        />
      </Content>
      {currentItem?.id && (
        <>
          {modalIsOpen && (
            <StickyConfirmModal
              isOpen={modalIsOpen}
              onRequestClose={modalCloseHandler}
              title='Удаление новости'
              okText='Удалить'
              text={
                <div>
                  Вы хотите убрать с портала и переместить в Корзину новость{' '}
                  <Typography.Text strong>
                    {currentItem.name || ''}
                  </Typography.Text>
                  ?
                </div>
              }
              onSubmit={() => handleRemove(currentItem.id)}
              okButtonProps={{ danger: true }}
            />
          )}
        </>
      )}
    </MainLayout>
  );
};
